import { createSelector } from 'reselect';

import { operationCanBind } from '../../types/Operation/Operation';
import containsString from '../../utils/containsString';
import sortObjectByKey from '../../utils/sortObjectByKey';
import { getAdminPageAgency, getAdminPageSearch } from '../adminPage/selectors';
import {
  getActivePageFromProps,
  getAipEndorsementYearFromProps,
  getAlphabetSortFromProps,
  getEditOperationSortFromProps,
  getInsurancePlanConverterFromProps,
  getOperationModalSelectorProps,
  getPerPageFromProps,
  getSearchFromProps,
  getSelectedAgencyFromProps
} from '../fromPropsSelector';
import { getOperationProducers } from '../lrp/selectors';
import { getOperationPageOperationData } from '../operationPage/selectors';

import type { Signer } from '../../scenes/lrp/containers/LrpPremiumQuoter/RequestCoverageModal/RequestCoverageModal.types';
import type InsurancePlan from '../../types/dto/InsurancePlan';
import type { RootState } from '../store';

export const getInsuranceAgencies = (state: RootState) => state.adminPanel.insuranceAgencies;

export const getAllInsuranceAgents = (state: RootState) => state.adminPanel.insuranceAgents;

export const getInsuranceAgents = (state: RootState) =>
  state.adminPanel.insuranceAgents[state.adminPage.agency?.name ?? ''];

export const getProducers = (state: RootState) =>
  state.adminPanel.producers[state.adminPage.agency?.name ?? ''];

export const getOperations = (state: RootState) => state.adminPanel.operations.entries;

export const getProducerOperations = (state: RootState) => state.adminPanel.producerOperations;

export const getOperationsProducers = (state: RootState) =>
  state.adminPanel.operationsProducers.entries;

export const getPreferencesUserPreferences = (state: RootState) =>
  state.adminPanel.userPreferences.preferences;

export const getAllAipEndorsements = (state: RootState) => state.adminPanel.aip.endorsements;

export const getAipStatus = (state: RootState) => state.adminPanel.aip.aipStatus;

export const getAipMatchedOperation = createSelector(getAipStatus, aipOperation => {
  if (!aipOperation) {
    return [];
  }
  return aipOperation.filter(element => element.operation.operationName);
});

export const getTotalUnmatchedEndorsementsCount = createSelector(getAipStatus, aipStatus => {
  const operationsWithUnmatched: string[] = [];
  let operationsFiltered: string[] = [];
  let counter = 0;

  for (const element of aipStatus) {
    if (
      element.operation.operationName &&
      element.isPrimaryContact &&
      element.unmatchedEndorsementsCount > 0
    ) {
      operationsWithUnmatched.push(element.operation.operationName);
    }
  }

  if (operationsWithUnmatched.length) {
    operationsFiltered = operationsWithUnmatched.filter(e =>
      operationsFiltered.includes(e) ? false : (operationsFiltered.push(e), true)
    );

    if (operationsFiltered.length) {
      counter = operationsFiltered.length;
    }
  }

  return counter;
});

export const getFilteredAgencies = createSelector(
  getInsuranceAgencies,
  getAdminPageSearch,
  (agencies, search) => {
    if (!agencies) {
      return [];
    }
    return agencies.filter(({ name }) => containsString(name, search));
  }
);

export const getAdminPanelAgencies = createSelector(
  getFilteredAgencies,
  getActivePageFromProps,
  getPerPageFromProps,
  (agencies, activePage, perPage) =>
    agencies.slice((activePage - 1) * perPage, perPage * activePage)
);

export const getAgencyEnabledPrograms = createSelector(
  getInsuranceAgencies,
  getSelectedAgencyFromProps,
  getInsurancePlanConverterFromProps,
  (agencies, selectedAgency, insurancePlanConverter) => {
    const agency = agencies.find(a => a.name === selectedAgency);
    const agencyInsurancePlanList: { name: string; id: string }[] = [];
    if (!agency) {
      return [];
    } else {
      const enabledPrograms = agency.insurancePrograms;
      for (const enabledProgram of enabledPrograms) {
        for (const insurancePlan of insurancePlanConverter) {
          if (enabledProgram === insurancePlan.id) {
            agencyInsurancePlanList.push({
              id: insurancePlan.id,
              name: insurancePlan.name
            });
          }
        }
      }
    }
    return agencyInsurancePlanList;
  }
);

export const getFilteredOperations = createSelector(
  getOperations,
  getAdminPageSearch,
  (operations, search) => {
    if (!operations) {
      return [];
    }

    return operations.filter(
      ({ name, fullName }) => containsString(name, search) || containsString(fullName, search)
    );
  }
);

export const getAdminPanelSortedOperations = createSelector(
  getFilteredOperations,
  getAlphabetSortFromProps,
  (operations, alphabetSort) => {
    let sortedOperations = [];
    if (alphabetSort !== 'All') {
      sortedOperations = operations.filter(operation =>
        operation.fullName.toLowerCase().startsWith(alphabetSort.toLowerCase())
      );
    } else {
      sortedOperations = operations;
    }

    return sortedOperations;
  }
);

export const getAdminPanelOperations = createSelector(
  getAdminPanelSortedOperations,
  getActivePageFromProps,
  getPerPageFromProps,
  (operations, activePage, perPage) =>
    operations.slice((activePage - 1) * perPage, perPage * activePage)
);

export const getOperationProducersModal = createSelector(
  getOperationsProducers,
  getAdminPageAgency,
  getOperationModalSelectorProps,
  (operationsProducers, agency, sortProps) => {
    const { selectedOperation, activePage, perPage, search } = sortProps;

    const noProducerListData = {
      length: 0,
      producerList: []
    };

    if (!operationsProducers) {
      return noProducerListData;
    }

    if (!operationsProducers[agency?.name ?? '']) {
      return noProducerListData;
    }

    const producers = operationsProducers[agency?.name ?? ''][selectedOperation];
    if (!producers) {
      return noProducerListData;
    }

    const sortedProducers = [...producers].filter(
      ({ firstName, lastName, email }) =>
        containsString(firstName, search) ||
        containsString(lastName, search) ||
        containsString(email, search)
    );

    return {
      length: sortedProducers.length,
      producerList: sortedProducers.slice((activePage - 1) * perPage, perPage * activePage)
    };
  }
);

export const getFilteredAgencyAgents = createSelector(
  getAllInsuranceAgents,
  getSearchFromProps,
  getSelectedAgencyFromProps,
  (insuranceAgents, search, selectedAgency) => {
    if (!insuranceAgents) {
      return [];
    }

    if (!insuranceAgents[selectedAgency]) {
      return [];
    }

    const agents = insuranceAgents[selectedAgency];

    if (!agents) {
      return [];
    }

    return agents.filter(
      ({ firstName, lastName, email }) =>
        containsString(firstName, search) ||
        containsString(lastName, search) ||
        containsString(email, search)
    );
  }
);

export const getEditAllOperations = createSelector(
  getOperations,
  getSearchFromProps,
  (operations, search) => operations.filter(({ fullName }) => containsString(fullName, search))
);

export const getEditAllPaginatedOperations = createSelector(
  getEditAllOperations,
  getActivePageFromProps,
  getPerPageFromProps,
  getEditOperationSortFromProps,
  (operations, activePage, perPage, sortProps) => {
    const { key, direction } = sortProps;
    const sortedOperations = [...operations].sort(sortObjectByKey(key, direction));
    return sortedOperations.slice((activePage - 1) * perPage, perPage * activePage);
  }
);

export const getFilteredAipEndorsements = createSelector(
  getAllAipEndorsements,
  getAipEndorsementYearFromProps,
  (endorsements, year) => {
    if (!endorsements) {
      return [];
    }
    const bothEndorsements = endorsements.filter(
      e => e.aipEndorsement !== undefined && e.endorsement !== undefined
    );
    const aipEndorsements = endorsements.filter(
      e => e.aipEndorsement !== undefined && e.endorsement === undefined
    );
    const drpEndorsements = endorsements.filter(
      e => e.aipEndorsement === undefined && e.endorsement !== undefined
    );

    const bothFiltered = [...bothEndorsements].filter(
      ({ aipEndorsement }) => aipEndorsement?.reinsuranceYear === year
    );
    const aipFiltered = [...aipEndorsements].filter(
      ({ aipEndorsement }) => aipEndorsement?.reinsuranceYear === year
    );
    const drpFiltered = [...drpEndorsements].filter(
      ({ endorsement }) => endorsement?.reinsuranceYear === year
    );

    return [...bothFiltered, ...aipFiltered, ...drpFiltered];
  }
);

export const getPaginatedAipEndorsements = createSelector(
  getFilteredAipEndorsements,
  getActivePageFromProps,
  getPerPageFromProps,
  (endorsements, activePage, perPage) =>
    endorsements
      .filter(x => x.aipEndorsement !== undefined || x.endorsement?.status === 'Purchased')
      .slice((activePage - 1) * perPage, perPage * activePage)
);

export const getOperationProducerSigners = (insurancePlan: InsurancePlan) =>
  createSelector(
    getOperationProducers,
    getOperationPageOperationData,
    (operationProducers, operation) => {
      if (!operationProducers) return [];
      if (!operation.agencyName) return [];

      const canOperationBind = insurancePlan && operationCanBind(operation, insurancePlan);
      return operationProducers
        .filter(producer => (canOperationBind ? producer.bindRequirements?.canBind : true))
        .map(
          x =>
            ({
              id: x.email,
              name: `${x.firstName} ${x.lastName}`,
              title: x.title
            }) as Signer
        );
    }
  );
